import React, { useState, useEffect } from 'react';
import { MdOutlineSendToMobile } from 'react-icons/md';
import analytics from '../utils/analytics';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  handleAppStoreClick,
  handlePlayStoreClick,
} from '../utils/appStoreLinks';
import QRModal from './QRModal';
import MobileAppDownloadModal from './MobileAppDownloadModal';
import './AppDownloadBanner.css';

const AppDownloadBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => {
    // Check if user has dismissed the banner
    const hasDismissed = localStorage.getItem('appDownloadBannerDismissed');
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.navigator.standalone === true ||
                  document.referrer.includes('android-app://');

    // Only show on PWA/web, not in native apps
    if (!hasDismissed && !isPWA) {
      setIsVisible(true);
    }
  }, []);

  // Expose banner presence for layout: floating UI and page bottoms clear
  // a 60px banner via --banner-clearance (see MapScreen.css, App.css).
  useEffect(() => {
    document.body.classList.toggle('has-bottom-banner', isVisible);
    return () => document.body.classList.remove('has-bottom-banner');
  }, [isVisible]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('appDownloadBannerDismissed', 'true');
  };

  const handleSendToPhone = () => {
    analytics.trackSendToPhoneClick();
    window.location.href = '/download';
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email) {
      // Create mailto link with app store URLs
      const subject = encodeURIComponent('Download The Crapper Mapper App');
      const body = encodeURIComponent(
        `Get the #1 App for your #2 Needs!\n\n` +
        `Download The Crapper Mapper:\n` +
        `iPhone/iPad: ${APP_STORE_URL}\n` +
        `Android: ${PLAY_STORE_URL}\n\n` +
        `Find and rate public restrooms anywhere!`
      );
      
      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
      setEmail('');
      setShowEmailForm(false);
    }
  };

  const handleDirectDownload = (store) => {
    analytics.trackAppStoreBadgeClick(store, 'banner');
    // Central referral handoff (clipboard copy on iOS) — never awaited so
    // the store window keeps its user-gesture context.
    if (store === 'ios') {
      handleAppStoreClick();
      window.open(APP_STORE_URL, '_blank');
    } else {
      handlePlayStoreClick();
      window.open(PLAY_STORE_URL, '_blank');
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <div className="app-download-banner">
        <div className="banner-content">
          <div className="left-section">
            <button 
              className="dismiss-btn"
              onClick={handleDismiss}
              aria-label="Dismiss banner"
            >
              ×
            </button>
            
            <img 
              src="/app-icon.png" 
              alt="The Crapper Mapper" 
              className="app-icon"
            />
            <div className="app-details">
              <h3 className="app-name">The Crapper Mapper</h3>
              <p className="tagline">Download the mobile app, now!</p>
            </div>
          </div>

          <div className="right-section">
            <button 
              className="send-to-phone-btn"
              onClick={handleSendToPhone}
              aria-label="Send to Phone"
            >
              <MdOutlineSendToMobile size={18} />
              Send to Phone
            </button>
            
            <div className="store-buttons">
              <button 
                className="store-badge ios"
                onClick={() => handleDirectDownload('ios')}
                aria-label="Download on the App Store"
              >
                <img 
                  src="/assets/badges/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg" 
                  alt="Download on the App Store" 
                  className="badge-svg"
                />
              </button>
              
              <button 
                className="store-badge android"
                onClick={() => handleDirectDownload('android')}
                aria-label="Get it on Google Play"
              >
                <img 
                  src="/assets/badges/GetItOnGooglePlay_Badge_Web_color_English.png" 
                  alt="Get it on Google Play" 
                  className="badge-png"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <QRModal 
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
      />
    </>
  );
};

export default AppDownloadBanner;
